import React, { useState, useEffect, useRef } from 'react'; // Added useRef
import { createPortal } from 'react-dom';
import styled from 'styled-components'; // Added styled-components
import {
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalTitle,
    CloseButton,
    Form,
    InputGroup,
    Label,
    Input,
    ButtonGroup,
    CancelButton,
    SaveButton,
    // TODO: Add styles for TagList, TagItem if different from AddSaveModal
} from '../../styles/AddBookmarkModal.styles'; // Reusing styles

// Predefined colors for tags - consider moving to a shared constants file
const PREDEFINED_COLORS = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
    '#F7DC6F', '#BB8FCE', '#F1948A', '#82E0AA', '#D7BDE2'
];

// Styled components for suggestions (can be moved to styles file later)
const SuggestionsPanel = styled.div`
    position: absolute;
    background-color: #2c2c2c;
    border: 1px solid #444;
    border-radius: 4px;
    max-height: 150px;
    overflow-y: auto;
    z-index: 1050;
    width: 100%;
    top: 100%;
    left: 0;
    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
`;

const SuggestionItem = styled.div`
    padding: 8px 12px;
    cursor: pointer;
    color: #e0e0e0;
    display: flex;
    align-items: center;
    &:hover {
        background-color: #383838;
    }
`;

const EditTagsModal = ({ isOpen, onClose, saveData, onSave, allUniqueTags = [] }) => { // Added allUniqueTags
    const [currentTags, setCurrentTags] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [selectedColor, setSelectedColor] = useState(PREDEFINED_COLORS[0]);
    const [loading, setLoading] = useState(false);

    const [suggestions, setSuggestions] = useState([]);
    const [isSuggestionsPanelOpen, setIsSuggestionsPanelOpen] = useState(false);
    const suggestionPanelRef = useRef(null);
    const tagInputRef = useRef(null);

    useEffect(() => {
        if (isOpen && saveData) { // Ensure modal is open and saveData is provided
            setCurrentTags(saveData.tags ? [...saveData.tags] : []);
        }
        // Reset fields when modal opens or saveData changes while open
        setTagInput('');
        setSelectedColor(PREDEFINED_COLORS[0]);
        setSuggestions([]);
        setIsSuggestionsPanelOpen(false);
    }, [isOpen, saveData]);


    const handleCloseModal = () => {
        onClose();
    };

    // Helper function to check if a tag already exists in currentTags (case insensitive)
    const tagExists = (name) => currentTags.some(tag => tag.name.toLowerCase() === name.toLowerCase());

    const handleAddTag = () => {
        const trimmedName = tagInput.trim();
        if (trimmedName !== '') {
            if (tagExists(trimmedName)) {
                alert("This tag has already been added.");
                setTagInput('');
                setIsSuggestionsPanelOpen(false);
                return;
            }
            setCurrentTags([...currentTags, { name: trimmedName, color: selectedColor }]);
            setTagInput('');
            setIsSuggestionsPanelOpen(false);
            tagInputRef.current?.focus();
        }
    };

    const handleRemoveTag = (indexToRemove) => {
        setCurrentTags(currentTags.filter((_, index) => index !== indexToRemove));
    };

    const handleTagInputChange = (e) => {
        const value = e.target.value;
        setTagInput(value);
        if (value.trim() === '') {
            setSuggestions([]);
            setIsSuggestionsPanelOpen(false);
        } else {
            // Filter unique tags that are not already in currentTags
            const filtered = allUniqueTags.filter(tag =>
                tag.name.toLowerCase().includes(value.toLowerCase()) &&
                !currentTags.some(ct => ct.name.toLowerCase() === tag.name.toLowerCase())
            );
            setSuggestions(filtered);
            setIsSuggestionsPanelOpen(filtered.length > 0);
        }
    };

    const openSuggestionsPanel = () => {
        if (tagInput.trim() !== '' && !isSuggestionsPanelOpen) {
            const filtered = allUniqueTags.filter(tag =>
                tag.name.toLowerCase().includes(tagInput.trim().toLowerCase()) &&
                !currentTags.some(ct => ct.name.toLowerCase() === tag.name.toLowerCase())
            );
            if (filtered.length > 0) {
                setSuggestions(filtered);
                setIsSuggestionsPanelOpen(true);
            }
        } else if (tagInput.trim() === '') {
            setSuggestions([]);
            setIsSuggestionsPanelOpen(false);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        // tagExists checks if the tag is already in currentTags
        if (tagExists(suggestion.name)) {
            alert("This tag has already been added.");
            setTagInput('');
        } else {
            setCurrentTags([...currentTags, { name: suggestion.name, color: suggestion.color }]);
            setTagInput('');
        }
        setSuggestions([]);
        setIsSuggestionsPanelOpen(false);
        tagInputRef.current?.focus();
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionPanelRef.current && !suggestionPanelRef.current.contains(event.target) &&
                tagInputRef.current && !tagInputRef.current.contains(event.target)) {
                setIsSuggestionsPanelOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []); // Empty dependency array means this effect runs once on mount and cleanup on unmount

    const handleSaveChanges = async () => {
        if (!saveData || !saveData.id) return;
        setLoading(true);
        try {
            await onSave(saveData.id, currentTags);
            handleCloseModal();
        } catch (error) {
            console.error("Error saving tags:", error);
            // Optionally, display an error message to the user
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !saveData) return null;

    const modalContent = (
        <ModalOverlay>
            <ModalContent>
                <ModalHeader>
                    <ModalTitle>Edit Tags for: {saveData.title || 'Save'}</ModalTitle>
                    <CloseButton onClick={handleCloseModal}>&times;</CloseButton>
                </ModalHeader>
                <Form onSubmit={(e) => { e.preventDefault(); handleSaveChanges(); }}>
                    {/* Display Current Tags */}
                    <InputGroup>
                        <Label>Current Tags:</Label>
                        <div style={{ marginBottom: '15px', display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                            {currentTags.length === 0 && <span style={{fontSize: '12px', color: '#888'}}>No tags yet.</span>}
                            {currentTags.map((tag, index) => (
                                <span key={index} style={{
                                    backgroundColor: tag.color,
                                    color: 'white',
                                    padding: '5px 8px',
                                    borderRadius: '4px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    fontSize: '12px'
                                }}>
                                    {tag.name}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveTag(index)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: 'white',
                                            marginLeft: '5px',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            lineHeight: '1'
                                        }}
                                    >
                                        &times;
                                    </button>
                                </span>
                            ))}
                        </div>
                    </InputGroup>

                    {/* Add New Tag Section */}
                    <InputGroup>
                        <Label htmlFor="edit-tag-input">Add New Tag:</Label>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <div style={{ flexGrow: 1, marginRight: '10px', position: 'relative' }}>
                                <Input
                                    ref={tagInputRef}
                                    id="edit-tag-input"
                                    type="text"
                                    value={tagInput}
                                    onChange={handleTagInputChange}
                                    onFocus={openSuggestionsPanel}
                                    placeholder="New tag name"
                                    style={{ width: '100%' }}
                                    autoComplete="off"
                                />
                                {isSuggestionsPanelOpen && suggestions.length > 0 && (
                                    <SuggestionsPanel ref={suggestionPanelRef} data-testid="tag-suggestions">
                                        {suggestions.map((suggestion, index) => (
                                            <SuggestionItem
                                                key={index}
                                                onMouseDown={() => handleSuggestionClick(suggestion)}
                                            >
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: '12px',
                                                    height: '12px',
                                                    borderRadius: '50%',
                                                    backgroundColor: suggestion.color,
                                                    marginRight: '8px'
                                                }}></span>
                                                {suggestion.name}
                                            </SuggestionItem>
                                        ))}
                                    </SuggestionsPanel>
                                )}
                            </div>
                            <div style={{ display: 'flex', marginRight: '10px', flexShrink: 0 }}>
                                {PREDEFINED_COLORS.map(color => (
                                    <button
                                        key={color}
                                        type="button"
                                        onClick={() => setSelectedColor(color)}
                                        title={color}
                                        style={{
                                            width: '24px',
                                            height: '24px',
                                            backgroundColor: color,
                                            border: selectedColor === color ? '2px solid #333' : '1px solid #ccc',
                                            marginRight: '5px',
                                            cursor: 'pointer',
                                            borderRadius: '50%'
                                        }}
                                    />
                                ))}
                            </div>
                            <button type="button" onClick={handleAddTag} style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                                Add Tag
                            </button>
                        </div>
                    </InputGroup>

                    {/* Display Available Tags for Quick Selection */}
                    <InputGroup>
                        <Label>Available Tags:</Label>
                        {allUniqueTags.length === 0 && <span style={{fontSize: '12px', color: '#888'}}>No tags available.</span>}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', maxHeight: '150px', overflowY: 'auto' }}>
                            {allUniqueTags.map((tag, index) => {
                                const isAdded = currentTags.some(t => t.name.toLowerCase() === tag.name.toLowerCase());
                                return (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => {
                                            if (!isAdded) {
                                                setCurrentTags([...currentTags, { name: tag.name, color: tag.color }]);
                                            }
                                        }}
                                        style={{
                                            backgroundColor: tag.color,
                                            color: 'white',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            border: 'none',
                                            cursor: isAdded ? 'not-allowed' : 'pointer',
                                            opacity: isAdded ? 0.6 : 1,
                                            fontSize: '12px'
                                        }}
                                        disabled={isAdded}
                                    >
                                        {tag.name} {isAdded && <span>✓</span>}
                                    </button>
                                );
                            })}
                        </div>
                    </InputGroup>
                    <ButtonGroup>
                        <CancelButton type="button" onClick={handleCloseModal} disabled={loading}>
                            Cancel
                        </CancelButton>
                        <SaveButton type="submit" disabled={loading}>
                            {loading ? 'Saving...' : 'Save Changes'}
                        </SaveButton>
                    </ButtonGroup>
                </Form>
            </ModalContent>
        </ModalOverlay>
    );

    return createPortal(modalContent, document.body);
};

// Additional functions for suggestions, similar to AddSaveModal
const EditTagsModalWithSuggestions = (props) => {
    const { allUniqueTags, currentTags: initialCurrentTags, isOpen } = props;
    // Note: currentTags from props is actually saveData.tags.
    // The component manages its own currentTags in state.

    const [suggestions, setSuggestions] = useState([]);
    const [isSuggestionsPanelOpen, setIsSuggestionsPanelOpen] = useState(false);
    // We need tagInput and other states to be managed within the main component scope,
    // so these handlers need to be defined there, or states passed around.
    // This wrapper approach is getting complicated. Let's integrate directly.

    // The logic below should be integrated into the main EditTagsModal component.
    // This is just for conceptual placement during refactoring.
    return <EditTagsModal {...props} />;
};


export default EditTagsModal;
